import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Star, MapPin, Briefcase, GraduationCap, Sparkles, EyeOff, Clock, Info, X } from "lucide-react";
import { Avatar } from "@/components/Avatar";
import { ProtectedMedia } from "@/components/ProtectedMedia";
import { VerifiedBadges } from "@/components/VerifiedBadges";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const NI_REASONS = ["Too Far", "Age Preference", "Profession", "Lifestyle", "Family Preference", "Already Contacted", "Not Compatible", "Other"];

function lastActive(iso) {
  if (!iso) return null;
  const diff = (Date.now() - new Date(iso).getTime()) / 60000;
  if (diff < 15) return "Online now";
  if (diff < 60) return "Active recently";
  if (diff < 1440) return `Active ${Math.floor(diff / 60)}h ago`;
  return `Active ${Math.floor(diff / 1440)}d ago`;
}

export function MatchCard({ p, onChanged }) {
  const nav = useNavigate();
  const [shortlisted, setShortlisted] = useState(p.shortlisted);
  const [interest, setInterest] = useState(p.interest_status);
  const [busy, setBusy] = useState(false);
  const [showWhy, setShowWhy] = useState(false);
  const [showReason, setShowReason] = useState(false);
  const score = p.compatibility?.score;
  const active = lastActive(p.last_active);
  const reasons = p.reasons || [];

  const act = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const sendInterest = () =>
    act(async () => {
      await api.post("/interests/send", { to_user: p.user_id });
      setInterest("pending");
      toast.success("Interest sent");
    });

  const toggleShortlist = () =>
    act(async () => {
      const { data } = await api.post(`/shortlist/${p.user_id}`);
      setShortlisted(data.shortlisted);
      toast.success(data.shortlisted ? "Added to shortlist" : "Removed from shortlist");
    });

  const ignore = () => setShowReason(true);

  const submitNotInterested = (reason) =>
    act(async () => {
      await api.post("/feedback/not-interested", { target: p.user_id, reason });
      setShowReason(false);
      toast.success("Thanks — we'll refine your recommendations");
      onChanged && onChanged(p.user_id);
    });

  return (
    <div className="mj-card overflow-hidden flex flex-col group animate-fadeup relative" data-testid={`match-card-${p.user_id}`}>
      <div className="relative aspect-[4/5] overflow-hidden bg-slate-100 cursor-pointer" onClick={() => nav(`/app/profile/${p.user_id}`)}>
        <ProtectedMedia className="w-full h-full">
          <Avatar photos={p.photos} name={p.full_name} size={9999} className="w-full h-full group-hover:scale-105 transition-transform duration-500" />
        </ProtectedMedia>
        <VerifiedBadgesFloat verification={p.verification} />
        {score != null && (
          <span className="absolute bottom-3 right-3 bg-[#3B0A12]/90 text-[#E2C382] border border-[#C59B27]/40 text-xs px-3 py-1 rounded-full backdrop-blur-sm font-semibold flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> {score}%
          </span>
        )}
        {p.premium && (
          <span className="absolute top-3 right-3 bg-[#C59B27] text-[#3B0A12] text-[10px] font-bold px-2 py-0.5 rounded-full">PREMIUM</span>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-lg font-semibold text-slate-900 leading-tight cursor-pointer hover:text-[#781826]" onClick={() => nav(`/app/profile/${p.user_id}`)}>
            {p.full_name}{p.age ? `, ${p.age}` : ""}
          </h3>
          <span className="text-[11px] text-[#9E7B1B] font-mono">{p.profile_code}</span>
        </div>
        <div className="mt-2 space-y-1 text-xs text-[#605255]">
          {(p.city || p.state) && <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#C59B27]" /> {[p.city, p.state].filter(Boolean).join(", ")}</div>}
          {p.occupation && <div className="flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5 text-[#C59B27]" /> {p.occupation}</div>}
          {p.qualification && <div className="flex items-center gap-1.5"><GraduationCap className="w-3.5 h-3.5 text-[#C59B27]" /> {p.qualification}</div>}
          {active && <div className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-emerald-600" /> <span className={active === "Online now" ? "text-emerald-600 font-medium" : ""}>{active}</span></div>}
        </div>
        {reasons.length > 0 && (
          <div className="mt-2">
            <button onClick={() => setShowWhy((s) => !s)} data-testid={`why-btn-${p.user_id}`} className="flex items-center gap-1 text-[11px] font-medium text-[#781826]">
              <Info className="w-3.5 h-3.5 text-[#C59B27]" /> Why this match?
            </button>
            {showWhy && (
              <ul className="mt-1.5 space-y-1 bg-[#FAF5EE] rounded-lg p-2" data-testid={`why-list-${p.user_id}`}>
                {reasons.slice(0, 4).map((r, i) => <li key={i} className="text-[11px] text-[#605255] flex gap-1.5"><Sparkles className="w-3 h-3 text-[#C59B27] shrink-0 mt-0.5" /> {r}</li>)}
              </ul>
            )}
          </div>
        )}
        <div className="mt-4 pt-3 border-t border-[#F1E9DA] flex items-center gap-2">
          <button data-testid={`interest-btn-${p.user_id}`} disabled={busy || interest === "pending" || interest === "accepted"} onClick={sendInterest}
            className="btn-primary flex-1 text-sm py-2 disabled:opacity-60">
            <Heart className="w-4 h-4" /> {interest === "accepted" ? "Connected" : interest === "pending" ? "Sent" : "Interest"}
          </button>
          <button data-testid={`shortlist-btn-${p.user_id}`} disabled={busy} onClick={toggleShortlist}
            className={`w-10 h-10 rounded-xl border flex items-center justify-center transition ${shortlisted ? "bg-[#FAF3E0] border-[#C59B27] text-[#C59B27]" : "border-[#E8DFD1] text-[#605255] hover:border-[#C59B27]"}`}>
            <Star className="w-4 h-4" fill={shortlisted ? "#C59B27" : "none"} />
          </button>
          <button data-testid={`ignore-btn-${p.user_id}`} disabled={busy} onClick={ignore} title="Not interested"
            className="w-10 h-10 rounded-xl border border-[#E8DFD1] text-[#605255] hover:border-[#BE123C] hover:text-[#BE123C] flex items-center justify-center">
            <EyeOff className="w-4 h-4" />
          </button>
        </div>
      </div>
      {showReason && (
        <div className="absolute inset-0 z-20 bg-white/97 flex flex-col p-4 rounded-2xl" data-testid={`ni-modal-${p.user_id}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="font-serif font-semibold text-[#58111A]">Why aren't you interested?</span>
            <button onClick={() => setShowReason(false)}><X className="w-4 h-4 text-[#605255]" /></button>
          </div>
          <div className="flex flex-wrap gap-1.5 overflow-y-auto">
            {NI_REASONS.map((r) => (
              <button key={r} disabled={busy} onClick={() => submitNotInterested(r)} data-testid={`ni-reason-${r.replace(/\s+/g, "-").toLowerCase()}-${p.user_id}`}
                className="text-xs px-2.5 py-1.5 rounded-full border border-[#E8DFD1] text-[#605255] hover:border-[#781826] hover:text-[#781826]">{r}</button>
            ))}
          </div>
          <p className="text-[10px] text-[#9E7B1B] mt-auto pt-2">We use this only to refine your recommendations. You can reset this anytime in Settings.</p>
        </div>
      )}
    </div>
  );
}

function VerifiedBadgesFloat({ verification }) {
  const count = Object.values(verification || {}).filter(Boolean).length;
  if (!count) return null;
  return (
    <div className="absolute top-3 left-3">
      <VerifiedBadges verification={verification} compact />
    </div>
  );
}
